import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import TextArea from "@/common/components/TextArea";
import { lastReviewSchema, LastReviewValidationSchema } from "@/common/schemas/lastReview.schema";
import { FormTransformers } from "@/common/utils/formTransformers";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useMaintenanceFiscalAssessmentDocumentDisapprove } from "../hooks/fiscalAssessmentDocuments/useMaintenanceFiscalAssessmentDocumentDisapprove";

interface FiscalAssessmentDocumentGroupDocumentDisapproveProps {
  documentId: string;
}

const FiscalAssessmentDocumentGroupDocumentDisapprove = ({
  documentId,
}: FiscalAssessmentDocumentGroupDocumentDisapproveProps) => {
  const { forwardingId } = useParams();

  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const {
    register,
    handleSubmit,
    setError,
    formState: {
      errors,
    },
  } = useForm<LastReviewValidationSchema>({
    resolver: zodResolver(lastReviewSchema),
    defaultValues: {
      state: 'disapproved',
    },
  });

  const {
    mutate: disapprove,
    isPending: disapproveIsPending,
  } = useMaintenanceFiscalAssessmentDocumentDisapprove({
    documentId,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['maintenanceForwarding', forwardingId],
        });

        setIsOpen(false);
      },
      onError: (error) => {
        FormTransformers.errorsTransformer({
          setError,
          setMessages,
        })(error);
      }
    }
  });

  const onSubmit: SubmitHandler<LastReviewValidationSchema> = (data) => disapprove(data);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        Trigger={(
          <Button
            variant="yellow"
            operationKlassToCheckPermission="Admin::Maintenance::FiscalAssessmentDocuments::Review"
            className="w-[120px]"
          >
            Declinar
          </Button>
        )}
        title="Reprovar nota fiscal"
        useCancellableFooter
        cancelButtonClassName="bg-white-200"
        confirmButtonClassName="bg-primary-blue"
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonIsLoading={disapproveIsPending}
        bodyClassName="text-white-800"
        body={(
          <div className="flex flex-col gap-4">
            <p className="text-base font-semibold">
              Essa nota fiscal será reprovado e após conclusão será devolvido para que o Estabelecimento realize correção
            </p>

            <TextArea
              label="Justificativa"
              markAsRequired
              rows={4}
              placeholder="Escreva aqui sua justificativa"
              {...register('description')}
              errorMessage={errors.description?.message}
            />
          </div>
        )}
      />

      <ModalMessageSimple
        messages={messages}
        setMessages={setMessages}
        isError
      />
    </>
  );
}

export default FiscalAssessmentDocumentGroupDocumentDisapprove;
